<template>
    <div class="small-text">
        <v-row class="mb-0" no-gutters>
            <v-col cols="1"></v-col>
            <v-col v-for="(day, i) in eventDays" :key="i">
                <v-card class="pa-2" outlined tile>
                    {{ day.heading }}
                </v-card>
            </v-col>
            <v-responsive width="100%" />
        </v-row>
        <v-row v-for="(time, j) in times" :key="j" class="mb-0" no-gutters>
            <v-col cols="1">
                <v-card class="pa-2 full-height" outlined tile>{{ time.text }}</v-card>
            </v-col>
            <v-col v-for="(day, i) in eventDays" :key="i">
                <v-card
                    v-if="
                        time.value === null ||
                            (time.value >= day.timeInStartMinutes &&
                                time.value <= day.timeInStartMinutes + day.timeInDurationMinutes)
                    "
                    class="full-height"
                    outlined
                    tile
                    v-on="isSelectMode ? { click: () => handleSelectClick(day.id, time) } : {}"
                    :class="isSelectMode ? 'select-mode' : ''"
                >
                    <div class="d-flex align-stretch full-height">
                        <div
                            v-if="showMonsterSlots"
                            :class="`monsterSlot monsterSlot-border-${$vuetify.theme.dark ? 'dark' : 'light'}`"
                        >
                            <monster-slot-marker :markerData="getMonsteSlotDataForTime(day.id, time.value)" class="" />
                        </div>
                        <div style="width: 100%">
                            <div
                                v-for="(data, k) in getPlaceHoldersDataForTime(day.id, time.value)"
                                :key="k"
                                class="place-holder"
                                v-on="readOnly ? {} : { click: () => handleAddClick(day.id, time, data.encounterType) }"
                            >
                                {{ data.text }}
                            </div>
                            <div
                                v-for="(data, k) in getSlotDataForTime(day.id, time.value)"
                                :key="k"
                                class="encounter-link-container"
                            >
                                <approval-status
                                    v-if="data.statusId !== undefined"
                                    :status="data.statusId"
                                ></approval-status>
                                <router-link
                                    v-if="readRouteName && !readOnly"
                                    :to="{ name: readRouteName, params: { id: data.id } }"
                                    :title="data.text"
                                    class="encounter-link"
                                >
                                    {{ data.text }}
                                </router-link>
                                <span v-else>{{ data.text }}</span>
                            </div>
                        </div>
                        <v-hover v-if="!readOnly">
                            <div
                                style="min-width:30px; height: 100%;"
                                slot-scope="{ hover }"
                                :class="`${hover ? 'active-card' : ''}`"
                            >
                                <v-btn
                                    v-if="!isSelectMode"
                                    class="hidden-button mt float-right"
                                    :class="`${hover ? 'show-button' : ''}`"
                                    icon
                                    dark
                                    small
                                    color="green"
                                    @click="handleAddClick(day.id, time)"
                                >
                                    <v-icon dark>
                                        mdi-plus
                                    </v-icon>
                                </v-btn>
                            </div>
                        </v-hover>
                    </div>
                </v-card>
                <v-card
                    v-else
                    class="pa-2 full-height"
                    outlined
                    tile
                    :color="$vuetify.theme.dark ? 'blue-grey darken-2' : 'grey lighten-2'"
                ></v-card>
            </v-col>
            <v-responsive width="100%" />
        </v-row>
    </div>
</template>
<script>
import { DateTime } from 'luxon'
import MonsterSlotMarker from './MonsterSlotMarker.vue'
import ApprovalStatusIcon from './ApprovalStatusIcon.vue'
export default {
    name: 'EventTimeTable',
    components: {
        'monster-slot-marker': MonsterSlotMarker,
        'approval-status': ApprovalStatusIcon,
    },
    props: {
        event: {
            type: Object,
            required: true,
        },
        slotData: {
            type: Array,
            required: false,
        },
        isSelectMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        showMonsterSlots: {
            type: Boolean,
            required: false,
            default: false,
        },
        monsterSlots: {
            type: Array,
            required: false,
        },
        placeHolders: {
            type: Array,
            required: false,
        },
        readRouteName: {
            type: String,
            required: false,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        eventDays() {
            if (!this.event.days) return []
            let eventDays = this.event.days.map(x => {
                x.heading = DateTime.fromISO(x.date, { zone: 'utc' }).toFormat('cccc')
                return x
            })
            return eventDays
        },
        times() {
            const minTimeInStart = Math.min(...this.event.days.map(x => x.timeInStartMinutes))
            const maxTimeOut = Math.max(...this.event.days.map(x => x.timeInStartMinutes + x.timeInDurationMinutes))
            const fullDayMinutes = 24 * 60
            let times = [
                {
                    text: 'Floating',
                    value: null,
                },
            ]
            let time = minTimeInStart
            while (time < maxTimeOut) {
                times.push({
                    text:
                        time < fullDayMinutes
                            ? `${this.zeroPad(Math.floor(time / 60))}:${this.zeroPad(time % 60)}`
                            : `${this.zeroPad(Math.floor((time - fullDayMinutes) / 60))}:${this.zeroPad(time % 60)}`,
                    value: time,
                })
                time += 30
            }
            return times
        },
        spreadMonsterSlots() {
            let spreadMonsterSlots = []
            if (!this.monsterSlots || this.monsterSlots.length === 0) return []
            this.monsterSlots.forEach(s => {
                for (var time = s.time; time < s.time + s.duration; time += 30) {
                    spreadMonsterSlots.push({
                        dayId: s.dayId,
                        text: s.text,
                        time: time,
                        isStart: time === s.time,
                        isEnd: time + 30 >= s.time + s.duration,
                    })
                }
            })
            return spreadMonsterSlots
        },
    },
    methods: {
        getTimeStamp(doc) {
            if (!this.isValidDate(doc.day.date)) return
            let dateOnly = DateTime.fromISO(doc.day.date, { zone: 'utc' })
            let dateTime = dateOnly.plus({ minutes: doc.timeAsMinutes })
            return `${dateOnly.toFormat('ccc')} ${dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)}`
        },
        zeroPad(value) {
            return ('0' + value).slice(-2)
        },
        getSlotDataForTime(dayId, time) {
            if (!this.slotData || this.slotData.length === 0) return []
            let data = this.slotData.filter(x => x.dayId === dayId && x.time === time)
            if (data && data.length > 0) {
                return data
            }
            return []
        },
        getMonsteSlotDataForTime(dayId, time) {
            if (!this.spreadMonsterSlots || this.spreadMonsterSlots.length === 0) return []
            let data = this.spreadMonsterSlots.filter(x => x.dayId === dayId && x.time === time)
            if (data && data.length > 0) {
                return [data[0]]
            }
            return []
        },
        getPlaceHoldersDataForTime(dayId, time) {
            if (!this.placeHolders || this.placeHolders.length === 0) return []
            let data = this.placeHolders.filter(x => x.dayId === dayId && x.time === time)
            if (data && data.length > 0) {
                return data
            }
            return []
        },
        handleAddClick(day, time, encounterType) {
            this.$emit('slotSelected', day, time ? time.value : null, encounterType ? encounterType : null)
        },
        handleSelectClick(day, time) {
            if (this.isSelectMode) this.$emit('slotSelected', day, time ? time.value : null)
        },
    },
}
</script>
<style scoped>
.full-height {
    height: 100%;
}
.small-text {
    font-size: 80%;
}
.active-card {
    background: lightgray;
}
.hidden-button {
    display: none;
}
.show-button {
    display: inline;
}
.monsterSlot {
    min-width: 45px;
    padding-right: 2px;
    margin-right: 4px;
    margin-left: 2px;
    height: 100%;
}
.monsterSlot-border-light {
    border-right: 2px solid rgba(0, 0, 0, 0.12);
}
.monsterSlot-border-dark {
    border-right: 2px solid rgba(255, 255, 255, 0.12);
}
.encounter-link-container {
    overflow: hidden;
}
.encounter-link:hover {
    background: rgba(255, 0, 0, 0.5);
}
.select-mode:hover {
    background: rgba(38, 0, 255, 0.5);
}
.place-holder:hover {
    color: red;
    cursor: pointer;
}
.place-holder {
    color: green;
}
</style>
